import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  ALINE_TOOL_NAMES,
  getExternalConnectDescription,
  getExternalConnectTitle,
  getExternalToolkitLabel,
} from '@aline/common';
import { CircularProgress, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { useExternalToolkitProviderConnect } from 'src/components/admin/integrations/use-external-toolkit-connect';
import { useChatAdapter } from 'src/components/ai/chat/ChatPanelMarkdown/ChatPanelMarkdown';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

import {
  CardPrimaryAction,
  CardSecondaryAction,
  PendingInteractionCardChrome,
} from './PendingInteractionCardChrome';

const useStyles = makeStyles()(theme => ({
  body: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.iconsType,
    fontSize: 14,
    whiteSpace: 'pre-wrap',
  },
  waiting: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  waitingText: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.appColors.secondaryType,
    fontSize: 14,
  },
}));

/**
 * The card a suspended turn is waiting on.
 *
 * Connecting happens on the provider's site. The shared connect hook watches
 * the catalogue until the account is live; this card then answers the pause
 * so the original request continues.
 */
export function ConnectExternalAccountCard({
  maxHeight,
  messageId,
  queuePosition,
  toolCallIds,
  toolkit,
}: {
  messageId: string;
  toolCallIds: readonly string[];
  toolkit: string;
  maxHeight?: number | null;
  queuePosition?: { current: number; total: number };
}) {
  const answered = useRef(false);
  const { addToolOutputs } = useChatAdapter();
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(true);
  const { awaitingProvider, busy, closeProviderTab, connect, isConnected } =
    useExternalToolkitProviderConnect(toolkit);

  const answerPause = useCallback(
    async (status: 'connected' | 'dismissed') => {
      if (answered.current) return;

      try {
        answered.current = true;
        await addToolOutputs(
          toolCallIds.map(toolCallId => ({
            messageId,
            output: { status, toolkit },
            tool: ALINE_TOOL_NAMES.connectExternalAccount,
            toolCallId,
          }))
        );
        closeProviderTab();
      } catch (error) {
        answered.current = false;
        Sentry.captureException(error);
        enqueueSnackbar('Could not continue the conversation. Please try again.', {
          variant: 'error',
        });
      }
    },
    [addToolOutputs, closeProviderTab, enqueueSnackbar, messageId, toolCallIds, toolkit]
  );

  useEffect(() => {
    if (!isConnected) return;
    void answerPause('connected');
  }, [answerPause, isConnected]);

  const handleConnect = useCallback(() => {
    void connect({ abort: () => answered.current });
  }, [connect]);

  const handleToggleExpanded = useCallback(() => {
    setExpanded(current => !current);
  }, []);

  return (
    <PendingInteractionCardChrome
      expanded={expanded}
      footer={
        isConnected ? undefined : (
          <>
            <CardSecondaryAction disabled={false} onClick={() => void answerPause('dismissed')}>
              Not now
            </CardSecondaryAction>
            <CardPrimaryAction disabled={busy} onClick={handleConnect}>
              Connect
            </CardPrimaryAction>
          </>
        )
      }
      maxHeight={maxHeight}
      title={
        queuePosition && queuePosition.total > 1
          ? `${getExternalConnectTitle(toolkit)} (${queuePosition.current} of ${queuePosition.total})`
          : getExternalConnectTitle(toolkit)
      }
      toggleLabel="account connection"
      onToggleExpanded={handleToggleExpanded}
    >
      <ConnectCardBody
        awaitingProvider={awaitingProvider}
        isConnected={isConnected}
        toolkit={toolkit}
      />
    </PendingInteractionCardChrome>
  );
}

function ConnectCardBody({
  awaitingProvider,
  isConnected,
  toolkit,
}: {
  awaitingProvider: boolean;
  isConnected: boolean;
  toolkit: string;
}): JSX.Element {
  const { classes } = useStyles();
  if (isConnected)
    return (
      <WaitingLine>
        {getExternalToolkitLabel(toolkit)} authenticated — continuing with the flow.
      </WaitingLine>
    );

  return (
    <>
      <Typography className={classes.body}>{getExternalConnectDescription(toolkit)}</Typography>
      {awaitingProvider && <WaitingLine>Waiting for you to finish signing in…</WaitingLine>}
    </>
  );
}

function WaitingLine({ children }: { children: React.ReactNode }): JSX.Element {
  const { classes } = useStyles();
  return (
    <div className={classes.waiting}>
      <CircularProgress size={14} />
      <Typography className={classes.waitingText} component="span">
        {children}
      </Typography>
    </div>
  );
}
